import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Avatar as MuiAvatar,
  Box,
  styled,
  TextareaAutosize,
  Typography,
} from '@mui/material';

import { InputValidationError, VehicleAssign } from '../../../../types';

import { useAppSelector } from '../../../../hooks';

import OrgAutocompleteBasic from '../../../../components/OrgAutocompleteBasic';
import Input from '../../../../components/TextField/Input';

import vehicleDefault from '../../../../assets/images/default-image/vehicle_default.png';
import UploadSvg from '../../../../assets/images/svg/btn_upload_image.svg';
import ErrorSvg from '../../../../assets/images/svg/ic_error_ifo.svg';
import {
  checkAlertInterval,
  checkNameFormat,
} from '../../management/validation/management-validation';

const Container = styled('div')`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const Content = styled('div')`
  flex: 1;
  overflow-y: auto;
  background-color: rgba(0, 0, 0, 0.2);
  padding: 0 10px 10px 10px;
  border-radius: 4px;
`;

const ContentTitle = styled(Typography)`
  color: white;
  line-height: 2.125rem;
`;

const SpanRequire = styled('span')`
  color: ${({ theme }) => theme.color.highlight};
`;

const Avatar = styled(MuiAvatar)`
  width: 200px;
  height: 200px;
  object-fit: cover;
  object-position: center;
  border-radius: 4px;
  margin: 0 auto;
`;

const ContainerDetails = styled('div')`
  margin: 10px 0;
  height: 58px;
`;

const Title = styled(Typography)(({ theme }) => ({
  ...theme.externalTypography.body3,
  color: theme.color.box_bbg,
}));

const ContainerImage = styled('div')`
  margin: 10px auto;
  width: 200px;
  position: relative;
`;

const ContainerUploadImage = styled('div')`
  cursor: pointer;
  position: absolute;
  bottom: 0;
  right: 0;
  width: 32px;
  height: 32px;
`;

const Error = styled(Box)`
  display: flex;
  flex-flow: row wrap;
  justify-content: center;
  align-items: center;
  & > span {
    color: #ff6b00;
    font-size: 0.75rem;
    line-height: 1.25rem;
  }
`;

const ContainerTextArea = styled('div')`
  & textarea {
    ${({ theme }) => ({ ...theme.typography.body2 })}
    width: 100%;
    font-family: 'Noto Sans TC', 'Noto Sans';
    background: ${({ theme }) => theme.color.secondary.$100};
    border-radius: 4px;
    outline: none;
    color: white;
    padding: 10px;
    border-color: transparent;
    resize: none;
    &:hover {
      background: ${({ theme }) => theme.color.secondary.$100};
    }
    &:focus {
      border: 1px solid rgba(0, 0, 0, 0.5);
    }
  }
`;

interface VehicleInformationProps {
  onChange: (data: VehicleAssign) => void;
  data: VehicleAssign | undefined;
}

const VehicleInformation: React.FC<VehicleInformationProps> = ({
  onChange,
  data,
}) => {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const { t } = useTranslation();

  const dialogRef = useRef<HTMLDivElement>(null);
  const orgMap = useAppSelector((store) => store.system.orgMap);
  const [imageFormatError, setImageFormatError] =
    useState<InputValidationError>({
      isError: false,
      message: '',
    });
  const [nameFormatError, setNameFormatError] = useState<InputValidationError>({
    isError: false,
    message: '',
  });
  const [orgIdError, setOrgIdError] = useState<InputValidationError>({
    isError: false,
    message: '',
  });
  const [alertIntervalFormatError, setAlertIntervalFormatError] =
    useState<InputValidationError>({
      isError: false,
      message: '',
    });

  const handleOnChange = (
    propertyObjs: Array<{ propertyName: keyof VehicleAssign; value: any }>,
  ) => {
    const currentValue = {
      ...data,
    };
    propertyObjs.forEach((obj) => {
      // @ts-ignore
      currentValue[obj.propertyName] = obj.value;
    });
    onChange({
      id: currentValue?.id ?? '',
      name: currentValue?.name ?? '',
      areaId: currentValue?.areaId ?? '',
      alertInterval: currentValue?.alertInterval ?? 5,
      imageURL: currentValue?.imageURL ?? '',
      orgId: currentValue?.orgId ?? '',
      bindingDastrackVs: currentValue?.bindingDastrackVs ?? [],
      plateNumber: currentValue?.plateNumber ?? undefined,
      groupIds: currentValue?.groupIds ?? [],
      remark: currentValue?.remark ?? undefined,
      avatarFile: currentValue.avatarFile,
      bindingDevices: [],
      imageUrl: null,
      type: 'vehicle',
    });
  };

  const handleOnClickUploadImage = () => {
    fileRef.current?.click();
  };

  const handleOnSelectFile = () => {
    setImageFormatError({ isError: false, message: '' });
    if (fileRef.current && fileRef.current?.files) {
      const fileImge = fileRef.current?.files[0];
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        const newSrc = reader.result as string;
        if (fileRef.current && fileRef.current.files) {
          handleOnChange([
            {
              propertyName: 'avatarFile',
              value: fileRef.current.files[0],
            },
            {
              propertyName: 'imageURL',
              value: newSrc,
            },
          ]);
        }
      });
      reader.readAsDataURL(fileImge);
    }
  };

  const LIST_ORG = useMemo(() => {
    const orgs = Object.values(orgMap);
    if (orgs.length > 0) {
      return orgs.map((organization) => ({
        id: organization?.id ?? '',
        name: organization?.name ?? '',
        color: organization?.color ?? '',
      }));
    }
    return [
      {
        id: '',
        name: '',
        color: '',
      },
    ];
  }, [orgMap]);

  return (
    <Container ref={dialogRef}>
      <ContentTitle>
        {t('project-settingJson.page.basic-information.title')}
        <SpanRequire>*</SpanRequire>
      </ContentTitle>
      <Content>
        <ContainerImage>
          <Avatar
            src={data?.imageURL ?? ''}
            alt="vehicle photo"
            sx={{ marginBottom: '10px' }}
            variant="square"
          >
            <Avatar src={vehicleDefault} alt="beeinventor" />
          </Avatar>
          <ContainerUploadImage onClick={handleOnClickUploadImage}>
            <img src={UploadSvg} alt="upload icon" />
            <input
              ref={fileRef}
              type="file"
              hidden
              accept=".jpg, .png, .jpeg, .svg"
              onChange={handleOnSelectFile}
              data-cy="input-image-vehicle"
            />
          </ContainerUploadImage>
        </ContainerImage>
        {imageFormatError.message && imageFormatError.message !== '' && (
          <Error>
            {<img src={ErrorSvg} alt="error" />}
            <span>{imageFormatError.message}</span>
          </Error>
        )}
        <ContainerDetails>
          <Title>
            {t('organization')}
            <SpanRequire>*</SpanRequire>
          </Title>
          <OrgAutocompleteBasic
            data={LIST_ORG}
            onSelect={(d) => {
              handleOnChange([
                {
                  propertyName: 'orgId',
                  value: d.id,
                },
              ]);
            }}
            onBlur={() => {
              if (!data?.orgId) {
                setOrgIdError({ isError: true, message: t('error') });
              } else {
                setOrgIdError({ isError: false, message: t('') });
              }
            }}
            error={orgIdError}
            selectedId={data?.orgId}
            data-cy="dropdown-org-vehicle"
            placeholder={t('project-settingJson.page.select-organization')}
          />
        </ContainerDetails>
        <ContainerDetails>
          <Title>
            {t('name')}
            <SpanRequire>*</SpanRequire>
          </Title>
          <Input
            name={t('name')}
            value={data?.name ?? ''}
            onChange={(e) => {
              handleOnChange([
                {
                  propertyName: 'name',
                  value: e.currentTarget.value,
                },
              ]);
            }}
            onBlur={() => setNameFormatError(checkNameFormat(data?.name ?? ''))}
            error={nameFormatError.isError}
            errorMessage={nameFormatError.message}
            autoComplete="off"
            data-cy="input-name-vehicle"
          />
        </ContainerDetails>
        <ContainerDetails>
          <Title>
            {t('alert-interval')}
            <SpanRequire>*</SpanRequire>
          </Title>
          <Input
            name={t('alert-interval')}
            value={data?.alertInterval ? data.alertInterval : 5}
            type="number"
            inputProps={{ min: 5, inputMode: 'numeric' }}
            onChange={(e) => {
              handleOnChange([
                {
                  propertyName: 'alertInterval',
                  value: Number(e.currentTarget.value),
                },
              ]);
            }}
            onBlur={() =>
              setAlertIntervalFormatError(
                checkAlertInterval(Number(data?.alertInterval ?? 0)),
              )
            }
            error={alertIntervalFormatError.isError}
            errorMessage={alertIntervalFormatError.message}
            autoComplete="off"
            data-cy="input-name-vehicle"
          />
        </ContainerDetails>
        <ContainerDetails>
          <Title>{t('license-plate')}</Title>
          <Input
            name={t('license-plate')}
            value={data?.plateNumber ?? ''}
            onChange={(e) => {
              handleOnChange([
                {
                  propertyName: 'plateNumber',
                  value: e.currentTarget.value,
                },
              ]);
            }}
            autoComplete="off"
            data-cy="input-name-vehicle"
          />
        </ContainerDetails>
        <ContainerTextArea>
          <Title>{t('remark')}</Title>
          <TextareaAutosize
            id="group-remark-input"
            name="group-remark"
            value={data?.remark ?? ''}
            maxRows={4}
            minRows={4}
            onChange={(e) => {
              handleOnChange([
                {
                  propertyName: 'remark',
                  value: e.currentTarget.value,
                },
              ]);
            }}
            autoComplete="off"
            data-cy="input-remark-vehicle"
          />
        </ContainerTextArea>
      </Content>
    </Container>
  );
};

export default VehicleInformation;
